import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { planStatusLabel } from './planConstants';

const TENANT_STATUSES = ['pending', 'trial', 'active', 'suspended', 'archived'];

export default function PlatformTenantsListPage() {
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [qInput, setQInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [planInput, setPlanInput] = useState('');
  const [createdFromInput, setCreatedFromInput] = useState('');
  const [createdToInput, setCreatedToInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [appliedPlanId, setAppliedPlanId] = useState('');
  const [appliedCreatedFrom, setAppliedCreatedFrom] = useState('');
  const [appliedCreatedTo, setAppliedCreatedTo] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await platformApiFetch('/api/platform/plans?status=active&pageSize=200&sort=sort_order&order=asc');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setPlans(json.plans || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('status', appliedStatus);
    if (appliedPlanId) params.set('planId', appliedPlanId);
    if (appliedCreatedFrom) params.set('createdFrom', appliedCreatedFrom);
    if (appliedCreatedTo) params.set('createdTo', appliedCreatedTo);
    try {
      const res = await platformApiFetch(`/api/platform/tenants?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load tenants');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus, appliedPlanId, appliedCreatedFrom, appliedCreatedTo, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
    setAppliedPlanId(planInput);
    setAppliedCreatedFrom(createdFromInput);
    setAppliedCreatedTo(createdToInput);
    setPage(0);
  };

  const toggleSort = (col) => {
    if (sort === col) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(col);
      setOrder('asc');
    }
    setPage(0);
  };

  const tenants = (data && data.tenants) || [];
  const total = (data && data.pagination && data.pagination.total) || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Tenants</Typography>
        {isSuper && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/platform/tenants/new')}>
            New tenant
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Name, code, email, domain…"
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {TENANT_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {planStatusLabel(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Plan</InputLabel>
            <Select
              label="Plan"
              value={planInput}
              onChange={(e) => setPlanInput(e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {plans.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.name} ({p.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Created from"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={createdFromInput}
            onChange={(e) => setCreatedFromInput(e.target.value)}
          />
          <TextField
            size="small"
            label="Created to"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={createdToInput}
            onChange={(e) => setCreatedToInput(e.target.value)}
          />
          <Button variant="outlined" onClick={applyFilters} disabled={loading}>
            Apply
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Button size="small" onClick={() => toggleSort('tenant_code')}>
                    Code {sort === 'tenant_code' ? (order === 'asc' ? '↑' : '↓') : ''}
                  </Button>
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => toggleSort('tenant_name')}>
                    Name {sort === 'tenant_name' ? (order === 'asc' ? '↑' : '↓') : ''}
                  </Button>
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => toggleSort('status')}>
                    Status {sort === 'status' ? (order === 'asc' ? '↑' : '↓') : ''}
                  </Button>
                </TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => toggleSort('created_at')}>
                    Created {sort === 'created_at' ? (order === 'asc' ? '↑' : '↓') : ''}
                  </Button>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <code>{t.tenant_code}</code>
                  </TableCell>
                  <TableCell>{t.tenant_name}</TableCell>
                  <TableCell>
                    <Chip size="small" label={planStatusLabel(t.status)} variant="outlined" />
                  </TableCell>
                  <TableCell>{t.plan_name || '—'}</TableCell>
                  <TableCell>{t.created_at ? String(t.created_at).slice(0, 10) : '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => navigate(`/platform/tenants/${t.id}`)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    No tenants match your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}
    </Box>
  );
}
